import { StyledSlideOutElement } from './SlideOut.styled'
import { useGlobalStore } from 'store/useGlobalStore'
import parse from 'html-react-parser'
import { HTMLText } from 'components/Share/HtmlText'
import { Spinner, OverlayTrigger, Popover } from 'react-bootstrap'
import { IIncentive, IVehicleCharges } from 'types/vehicleTypes'
import { useRef } from 'react'
import { useBiddingSidePanel } from 'store/useBiddingSidePanelStore'
import { normalizeCurrencySymbol } from 'utils/currencyUtils'

interface IProps {
  feeLoading: boolean // loader for generic buyer premium
  buyerFeeString: string
  deliveryCharge: number
  vehicleCharges: IVehicleCharges[]
  isIncentiveAccumulate: boolean
  incentives: IIncentive[]
  estimatedGrossPrice: number
  hasAnyChargeRange: boolean
  buyItNowPriceString?: string
  purchaseExclusivePriceString?: string
  relistFeeString?: string
}

export const SlideOutPricingDetails = ({
  buyerFeeString,
  relistFeeString,
  buyItNowPriceString,
  purchaseExclusivePriceString,
  deliveryCharge,
  estimatedGrossPrice,
  vehicleCharges,
  isIncentiveAccumulate,
  incentives,
  feeLoading,
  hasAnyChargeRange
}: IProps) => {
  const getLocalText = useGlobalStore((state) => state.getLocalText)
  const isBuyItNow = useBiddingSidePanel((state) => state.isBuyItNow)
  const isPurchaseExclusive = useBiddingSidePanel((state) => state.isPurchaseExclusive)
  const slideOutElementRef = useRef(null)
  const totalTransactionChargesText = isPurchaseExclusive ? 'Buyer Fee' : getLocalText('TOTAL_TRANSACTION_CHARGES')
  const totalPriceText = isBuyItNow
    ? getLocalText('TOTAL_GROSS_PRICE')
    : isPurchaseExclusive
    ? 'Total Price'
    : getLocalText('ESTIMATED_GROSS_PRICE')

  const termsAndConditionsText = getLocalText('TERMS_CONDIIONS_AGREE')?.replace(
    '&lt;a href=&quot;#terms-modal&quot; data-toggle=&quot;modal&quot;&gt;Terms and Conditions&lt;/a&gt;',
    '&lt;a href=&quot;/public#terms&quot; target=&quot;_blank&quot; rel=&quot;noopener noreferrer&quot;&gt;Terms and Conditions&lt;/a&gt;'
  )

  return (
    <StyledSlideOutElement ref={slideOutElementRef}>
      {isBuyItNow && (
        <p>
          <b>{getLocalText('BUY_IT_NOW_PRICE_LABEL')}</b>
          {':'}
          &nbsp;
          {normalizeCurrencySymbol(buyItNowPriceString) || 'N/A'}
        </p>
      )}
      {isPurchaseExclusive && (
        <p>
          <b>Price</b>
          {':'}
          &nbsp;
          {normalizeCurrencySymbol(purchaseExclusivePriceString) || 'N/A'}
        </p>
      )}
      <p>
        <b>{totalTransactionChargesText}</b>
        {':'}
        &nbsp;
        {feeLoading ? (
          <Spinner size="sm" animation="border"></Spinner>
        ) : (
          normalizeCurrencySymbol(buyerFeeString) || 'N/A'
        )}
        &nbsp;
        {hasAnyChargeRange && !isBuyItNow && !isPurchaseExclusive && (
          <OverlayTrigger
            rootClose={true}
            container={slideOutElementRef}
            trigger="click"
            placement="bottom"
            overlay={
              <Popover id="popover-basic">
                <Popover.Title as="h3">{getLocalText('VIEW_FEES_TEXT')}</Popover.Title>
                <Popover.Content>{getLocalText('VIEW_FEES_INFORMATION')}</Popover.Content>
              </Popover>
            }
          >
            <span className="fee-info">({getLocalText('VIEW_FEES_TEXT')})</span>
          </OverlayTrigger>
        )}
      </p>
      {isPurchaseExclusive && relistFeeString !== undefined && relistFeeString !== null && relistFeeString !== '' && (
        <p>
          <b>Re-list Fee:</b> {relistFeeString ? normalizeCurrencySymbol(relistFeeString) : 'N/A'}
        </p>
      )}
      {!isPurchaseExclusive && (
        <>
          <p>
            <b>Total Delivery Charge:</b>{' '}
            {deliveryCharge ? '$' + new Intl.NumberFormat().format(deliveryCharge) : 'N/A'}
          </p>
          {vehicleCharges?.map((charge, index) => (
            <p key={index}>
              <b>{charge.VehicleChargeTypeDescription}:</b> {charge.PriceString}
            </p>
          ))}
        </>
      )}
      {!isIncentiveAccumulate &&
        incentives?.map((incentive, index) => (
          <p key={index}>
            <b>{incentive.Name}:</b> {incentive.AmountString}
          </p>
        ))}
      <p>
        <b>{totalPriceText}</b>
        {':'}
        &nbsp;
        {isNaN(estimatedGrossPrice) ? (
          'N/A'
        ) : feeLoading ? (
          <Spinner size="sm" animation="border"></Spinner>
        ) : (
          '$' + new Intl.NumberFormat().format(estimatedGrossPrice)
        )}
      </p>
      {!isPurchaseExclusive && (
        <div className="terms-and-conditions">
          <HTMLText value={parse(termsAndConditionsText)}></HTMLText>
        </div>
      )}
    </StyledSlideOutElement>
  )
}
